import type { consoleBffKintoneEnvironment } from "@kintone-storage/openapi-types";
import { apiClient } from "./client";

type Schemas = consoleBffKintoneEnvironment.components["schemas"];

export type KintoneEnvironmentCurrent = Schemas["KintoneEnvironmentCurrentResponse"];
export type KintoneConnectionAccess = Schemas["ConnectionAccess"];
export type OwnershipVerificationRequest =
  | Schemas["NewApiTokenOwnershipVerificationRequest"]
  | Schemas["SavedAppCredentialsOwnershipVerificationRequest"]
  | Schemas["AdministratorOwnershipVerificationRequest"];
export type OwnershipVerificationResponse = Schemas["OwnershipVerificationResponse"];
export type OwnershipScanContinuationResponse = Schemas["OwnershipScanContinuationResponse"];
export type OwnershipVerificationResult =
  | OwnershipVerificationResponse
  | OwnershipScanContinuationResponse;
export type InitialEnvironmentRegistrationRequest = Schemas["InitialEnvironmentRegistrationRequest"];
export type CreateEnvironmentSwitchOperationRequest =
  Schemas["CreateEnvironmentSwitchOperationRequest"];
export type ConfirmEnvironmentSwitchRequest = Schemas["ConfirmEnvironmentSwitchRequest"];
export type EnvironmentSwitchOperation = Schemas["EnvironmentSwitchOperationResponse"];
export type EnvironmentAppReconciliation = Schemas["EnvironmentAppReconciliation"];
export type ReconciliationMutationRequest = Schemas["ReconciliationMutationRequest"];
export type NameSyncRequest = Schemas["NameSyncRequest"];
export type SecureAccessSetting = Schemas["SecureAccessSettingResponse"];
export type ReplaceSecureAccessCertificateRequest =
  Schemas["ReplaceSecureAccessCertificateRequest"];
export type DisableSecureAccessRequest = Schemas["DisableSecureAccessRequest"];
export type SecureAccessVerification = Schemas["SecureAccessVerification"];
export type SecureAccessMutationResult =
  | SecureAccessSetting
  | OwnershipScanContinuationResponse;

export function fetchCurrentKintoneEnvironment() {
  return apiClient<KintoneEnvironmentCurrent>("/kintone-environment/current");
}

export function verifyKintoneEnvironmentOwnership(body: OwnershipVerificationRequest) {
  return apiClient<OwnershipVerificationResult>("/kintone-environment/ownership-verifications", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function registerInitialKintoneEnvironment(
  body: InitialEnvironmentRegistrationRequest,
  idempotencyKey: string,
) {
  return apiClient<KintoneEnvironmentCurrent>("/kintone-environment/registration", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function fetchSecureAccessSetting() {
  return apiClient<SecureAccessSetting>("/console-bff/secure-access");
}

export function replaceSecureAccessCertificate(
  body: ReplaceSecureAccessCertificateRequest,
  idempotencyKey: string,
) {
  return apiClient<SecureAccessMutationResult>("/console-bff/secure-access", {
    method: "PUT",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function disableSecureAccess(
  body: DisableSecureAccessRequest,
  idempotencyKey: string,
) {
  return apiClient<SecureAccessMutationResult>("/console-bff/secure-access", {
    method: "DELETE",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function createKintoneEnvironmentSwitchOperation(
  body: CreateEnvironmentSwitchOperationRequest,
  idempotencyKey: string,
) {
  return apiClient<EnvironmentSwitchOperation>("/kintone-environment/switch-operations", {
    method: "POST",
    headers: { "Idempotency-Key": idempotencyKey },
    body: JSON.stringify(body),
  });
}

export function fetchKintoneEnvironmentSwitchOperation(operationId: string) {
  return apiClient<EnvironmentSwitchOperation>(
    `/kintone-environment/switch-operations/${operationId}`,
  );
}

export function confirmKintoneEnvironmentSwitchOperation(
  operationId: string,
  body: ConfirmEnvironmentSwitchRequest,
) {
  return apiClient<EnvironmentSwitchOperation>(
    `/kintone-environment/switch-operations/${operationId}/confirmations`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
}

export function cancelKintoneEnvironmentSwitchOperation(
  operationId: string,
  expectedOperationRevision: number,
  idempotencyKey: string,
) {
  return apiClient<EnvironmentSwitchOperation>(
    `/kintone-environment/switch-operations/${operationId}/cancellations`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify({ expectedOperationRevision }),
    },
  );
}

export function recheckKintoneEnvironmentAppReconciliation(
  operationId: string,
  appBindingId: string,
  body: ReconciliationMutationRequest,
  idempotencyKey: string,
) {
  return apiClient<EnvironmentAppReconciliation>(
    `/kintone-environment/switch-operations/${operationId}/reconciliations/${appBindingId}/rechecks`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(body),
    },
  );
}

export function syncKintoneEnvironmentAppName(
  operationId: string,
  appBindingId: string,
  body: NameSyncRequest,
  idempotencyKey: string,
) {
  return apiClient<EnvironmentAppReconciliation>(
    `/kintone-environment/switch-operations/${operationId}/reconciliations/${appBindingId}/name-sync`,
    {
      method: "POST",
      headers: { "Idempotency-Key": idempotencyKey },
      body: JSON.stringify(body),
    },
  );
}
